import React, { useRef, useState, useEffect } from 'react';
import ActionMessage from '../../../components/ActionMessage/ActionMessage';
import SelectSymbol from '../../../components/SelectSymbol/SelectSymbol';
import SwitchInput from '../../../components/SwitchInput/SwitchInput';
import { processError } from '../../../services/ServiceUtils';
import { saveStrategy, getStrategy, addSupportingAutomation, removeSupportingAutomation } from '../../../services/StrategiesService';
import { initializeModal } from '../../../utils/ModalUtils';
import { DEFAULT_STRATEGY } from '../../../utils/StrategiesUtils';
import SelectAutomation from './SelectAutomation';
import SelectMonitor from './SelectMonitor';
import ShareInput from './ShareInput';

/**
 * props:
 * - data
 * - onSubmit
 */
function StrategyModal(props) {
    const [error, setError] = useState('');
    const [strategy, setStrategy] = useState(DEFAULT_STRATEGY);
    const [addingSupport, setAddingSupport] = useState(false);
    const [newSupportId, setNewSupportId] = useState('');
    // Alvo da estratégia (só na criação): 'single' = fluxo manual (liga peças de 1 símbolo);
    // 'list'/'auto' = setup GERADO → ponte pro Setup AI Full (não dá pra montar manualmente).
    const [targetMode, setTargetMode] = useState('single');

    const btnClose = useRef('');
    const btnSave = useRef('');

    useEffect(() => {
        initializeModal('modalStrategy', () => { setStrategy({ ...DEFAULT_STRATEGY }); setTargetMode('single'); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Vários ativos/automático não se monta ligando peças prontas — fecha este modal e
    // abre o gerador do AI Full já com o alvo escolhido (ver Strategies.onUseAiFull).
    function goToAiFull() {
        btnClose.current.click();
        if (props.onUseAiFull) props.onUseAiFull(targetMode);
    }

    useEffect(() => {
        if (!props.data || !props.data.id) {
            setStrategy({ ...DEFAULT_STRATEGY });
        } else {
            setStrategy(props.data);
            getStrategy(props.data.id)
                .then(full => setStrategy(full))
                .catch(() => {});
        }
    }, [props.data]);

    function onSubmit(event) {
        saveStrategy(strategy.id, strategy)
            .then(result => {
                btnClose.current.click();
                if (props.onSubmit) props.onSubmit(result);
            })
            .catch(err => setError(processError(err)))
        props.onSubmit(event);
    }

    function onInputChange(event) {
        setStrategy(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    function refreshSupporting() {
        if (!strategy.id) return;
        getStrategy(strategy.id)
            .then(full => setStrategy(prev => ({ ...prev, supportingAutomations: full.supportingAutomations })))
            .catch(() => {});
    }

    function onRemoveSupporting(automationId) {
        removeSupportingAutomation(strategy.id, automationId)
            .then(refreshSupporting)
            .catch(err => setError(processError(err)));
    }

    function onAddSupporting() {
        if (!newSupportId) return;
        setAddingSupport(true);
        addSupportingAutomation(strategy.id, newSupportId)
            .then(() => { setNewSupportId(''); refreshSupporting(); })
            .catch(err => setError(processError(err)))
            .finally(() => setAddingSupport(false));
    }

    return (
        <div className="modal fade" id="modalStrategy" tabIndex="-1" role="dialog" aria-labelledby="modalTitleNotify" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">{props.data.id ? 'Editar ' : 'Nova '}Estratégia</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>

                    <div className="modal-body">
                        {!strategy.id && (
                            <div className='mb-3'>
                                <label className='form-label small fw-bold mb-1'>Alvo da estratégia:</label>
                                <div className='btn-group d-flex' role='group'>
                                    <button type='button' className={`btn btn-sm ${targetMode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTargetMode('single')}>Um símbolo</button>
                                    <button type='button' className={`btn btn-sm ${targetMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTargetMode('list')}>Vários ativos</button>
                                    <button type='button' className={`btn btn-sm ${targetMode === 'auto' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTargetMode('auto')}>Automático</button>
                                </div>
                            </div>
                        )}
                        {!strategy.id && targetMode !== 'single' && (
                            <div className='alert alert-info py-3 small'>
                                <p className='mb-2'>
                                    {targetMode === 'list'
                                        ? <><strong>Vários ativos:</strong> uma estratégia replicada na lista de ativos que você escolher.</>
                                        : <><strong>Automático:</strong> o scanner escolhe os ativos e a estratégia acompanha sozinha.</>}
                                    {' '}Isso não se monta ligando peças manualmente — usa o gerador <strong>Setup AI Full</strong> (IA + scanner), que cria monitores, automações e templates de ordem automaticamente.
                                </p>
                                <button type='button' className='btn btn-sm btn-primary' onClick={goToAiFull}>
                                    Continuar no Setup AI Full →
                                </button>
                            </div>
                        )}
                        {(strategy.id || targetMode === 'single') &&
                        <div className="form-group">
                            <div className='row'>
                                <div className='col-md-6 mb-3'>
                                    <div className="form-group">
                                        <label htmlFor='symbol'>Símbolo:</label>
                                        <SelectSymbol onChange={onInputChange} symbol={strategy.symbol} disabled={strategy.id > 0} />
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-md-12 mb-3'>
                                    <div className='form-group'>
                                        <label htmlFor='name'>Nome:</label>
                                        <input type='text' id='name' className='form-control' placeholder='Nome da estratégia' onChange={onInputChange} value={strategy.name ? strategy.name : ''} />
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-md-12 mb-3'>
                                    <div className='form-group'>
                                        <label htmlFor='monitorId'>Monitor:</label>
                                        <SelectMonitor symbol={strategy.symbol} monitorId={strategy.monitorId} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-md-12 mb-3'>
                                    <div className='form-group'>
                                        <label htmlFor='buyAutomationId'>Automação de Compra:</label>
                                        <SelectAutomation symbol={strategy.symbol} id='buyAutomationId' automationId={strategy.buyAutomationId} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-md-12 mb-3'>
                                    <div className='form-group'>
                                        <label htmlFor='sellAutomationId'>Automação de Venda:</label>
                                        <SelectAutomation symbol={strategy.symbol} id='sellAutomationId' automationId={strategy.sellAutomationId} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            {strategy.id > 0 && (
                                <div className='row'>
                                    <div className='col-md-12 mb-3'>
                                        <label>Automações de Suporte:</label>
                                        <div className='d-flex flex-column gap-1 mt-1'>
                                            {(strategy.supportingAutomations || []).map(auto => (
                                                <div key={auto.id} className='d-flex align-items-center gap-2' style={{ fontSize: 13 }}>
                                                    <span className={`badge ${auto.isActive ? 'bg-success' : 'bg-danger'}`} style={{ minWidth: 62 }}>
                                                        {auto.isActive ? (auto.schedule ? 'AGENDADA' : 'ATIVA') : 'PARADA'}
                                                    </span>
                                                    <span className='flex-grow-1'>{auto.name}</span>
                                                    <button type='button' className='btn btn-sm btn-outline-danger py-0 px-1'
                                                        style={{ fontSize: 11 }} title='Desvincular'
                                                        onClick={() => onRemoveSupporting(auto.id)}>✕</button>
                                                </div>
                                            ))}
                                            <div className='d-flex align-items-center gap-2 mt-1'>
                                                <div className='flex-grow-1'>
                                                    <SelectAutomation symbol={strategy.symbol} id='newSupportId'
                                                        automationId={newSupportId}
                                                        onChange={e => setNewSupportId(e.target.value)} />
                                                </div>
                                                <button type='button' className='btn btn-sm btn-outline-primary py-0 px-2'
                                                    style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                                                    disabled={!newSupportId || addingSupport}
                                                    onClick={onAddSupporting}>
                                                    {addingSupport ? '...' : '+ Vincular'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <ShareInput sharedWith={strategy.sharedWith} onChange={onInputChange} />
                            <div className='row'>
                                <div className='col-md-6'>
                                    <div className='form-group'>
                                        <SwitchInput id='isActive' text='Ativa?' onChange={onInputChange} isChecked={strategy.isActive} />
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </div>
                    <div className="modal-footer">
                        <ActionMessage error={error} extraClassName="mt-1 col-9 py-1" />
                        {(strategy.id || targetMode === 'single') && (
                            <button ref={btnSave} type="button" className="btn btn-sm btn-primary" onClick={onSubmit}>
                                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" /></svg>
                                Salvar
                            </button>
                        )}
                    </div>

                </div>
            </div>
        </div>
    )
}


export default StrategyModal;